import { useState } from 'react';
import { Link } from 'react-router';
import { Award, Download, ExternalLink, Loader2, ChevronRight, Star, X } from 'lucide-react';
import { useEnrollments } from '../hooks/useEnrollments';
import { useAuthStore } from '../store/auth';
import type { LearningResult } from '@lms/shared/types';
import { useLanguage } from '../lib/LanguageContext';
import { Bilingual } from '../components/Bilingual';
import { copy } from '../copy/copy';

interface CertWithMeta extends LearningResult {
  programName: string;
  programId: string;
  enrollmentStatus: string;
}

export function Certificates() {
  const { user } = useAuthStore();
  const { data: enrollments, isLoading } = useEnrollments({ userId: user?.id });
  const { t } = useLanguage();
  const [selectedCert, setSelectedCert] = useState<CertWithMeta | null>(null);

  const certificates: CertWithMeta[] = (enrollments ?? [])
    .flatMap(enrollment => {
      const certResults = (enrollment.results ?? []).filter(r => r.type === 'CERTIFICATE');
      return certResults.map(cert => ({
        ...cert,
        programName: enrollment.program?.name ?? t(copy.common.programFallback),
        programId: enrollment.programId,
        enrollmentStatus: enrollment.status,
      }));
    })
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] space-y-4">
        <Loader2 className="w-8 h-8 text-primary animate-spin" />
        <p className="text-sm font-body text-on-surface-variant">
          <Bilingual {...copy.certificates.loading} />
        </p>
      </div>
    );
  }

  if (certificates.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] py-20 text-center space-y-6 animate-fadeIn">
        <div className="bg-surface-container rounded-full w-24 h-24 flex items-center justify-center mx-auto">
          <Award size={40} className="text-on-surface-variant/30" />
        </div>
        <h2 className="text-2xl font-headline font-bold text-on-surface">
          <Bilingual {...copy.certificates.emptyTitle} />
        </h2>
        <p className="text-on-surface-variant max-w-md leading-relaxed">
          <Bilingual {...copy.certificates.emptyDesc} />
        </p>
        <Link
          to="/programs"
          className="inline-flex items-center gap-2 text-primary font-label font-bold uppercase tracking-widest text-xs hover:underline pt-4"
        >
          <Bilingual {...copy.certificates.browsePrograms} />
          <ChevronRight size={16} />
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fadeIn pb-12">
      <div className="relative overflow-hidden bg-primary/10 rounded-3xl p-8 mb-4 border border-primary/5">
        <div className="relative z-10">
          <h1 className="text-4xl font-headline font-bold text-on-surface mb-2 tracking-tight">
            <Bilingual {...copy.certificates.title} />
          </h1>
          <p className="text-on-surface-variant max-w-lg leading-relaxed">
            <Bilingual {...copy.certificates.subtitle} />
          </p>
          <p className="text-sm font-bold text-primary mt-3">
            {certificates.length} <Bilingual {...copy.certificates.totalLabel} />
          </p>
        </div>
        <div className="absolute top-0 right-0 p-8 text-primary/10">
          <Star size={120} />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {certificates.map(cert => {
          const downloadUrl = (cert.metadata as Record<string, unknown> | null)?.downloadUrl as
            | string
            | undefined;

          return (
            <div
              key={cert.id}
              onClick={() => setSelectedCert(cert)}
              className="bg-surface-container-lowest rounded-3xl p-6 shadow-level1 hover:shadow-level2 transition-all duration-300 group border border-on-surface/5 flex flex-col hover:-translate-y-1 cursor-pointer"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="p-3 bg-secondary/10 rounded-2xl text-secondary group-hover:bg-secondary group-hover:text-on-secondary transition-all">
                  <Award size={28} />
                </div>
                <div className="px-3 py-1 bg-success/10 text-success text-[10px] font-bold uppercase tracking-widest rounded-full">
                  <Bilingual {...copy.certificates.earned} />
                </div>
              </div>

              <div className="flex-1">
                <h3 className="font-headline font-bold text-on-surface text-lg group-hover:text-primary transition-colors tracking-tight mb-2">
                  {cert.programName}
                </h3>
                <p className="text-on-surface-variant text-sm mb-6 leading-relaxed line-clamp-2">
                  {cert.title}
                </p>
              </div>

              <div className="pt-5 border-t border-on-surface/5 flex items-center justify-between">
                <span className="text-[10px] font-label text-on-surface-variant uppercase tracking-widest">
                  {new Date(cert.createdAt).toLocaleDateString(t({ id: 'id-ID', ar: 'ar-SA' }), {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })}
                </span>
                {downloadUrl && (
                  <a
                    href={downloadUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={e => e.stopPropagation()}
                    className="p-2 bg-primary/10 text-primary rounded-xl hover:bg-primary hover:text-on-primary transition-all"
                    title={t(copy.certificates.download)}
                  >
                    <Download size={16} />
                  </a>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {selectedCert && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fadeIn"
          onClick={() => setSelectedCert(null)}
        >
          <div
            className="bg-surface-container-lowest border border-on-surface/5 rounded-3xl p-8 max-w-lg w-full shadow-level4 space-y-6 animate-scaleIn"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex justify-between items-start">
              <div className="flex gap-4 items-start">
                <div className="p-4 bg-secondary/10 text-secondary rounded-2xl">
                  <Award size={32} />
                </div>
                <div className="space-y-2">
                  <h3 className="text-xl font-headline font-bold text-on-surface">
                    {selectedCert.programName}
                  </h3>
                  <p className="text-sm text-on-surface-variant">{selectedCert.title}</p>
                  <p className="text-xs text-on-surface-variant/60">
                    {new Date(selectedCert.createdAt).toLocaleDateString(
                      t({ id: 'id-ID', ar: 'ar-SA' }),
                      { year: 'numeric', month: 'long', day: 'numeric' }
                    )}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setSelectedCert(null)}
                className="p-2 hover:bg-surface-container rounded-xl transition-colors"
              >
                <X size={20} className="text-on-surface-variant" />
              </button>
            </div>

            {selectedCert.description && (
              <div className="p-4 bg-surface-container-low rounded-2xl text-sm text-on-surface-variant leading-relaxed">
                {selectedCert.description}
              </div>
            )}

            <div className="flex gap-3 pt-2">
              {(selectedCert.metadata as Record<string, unknown> | null)?.downloadUrl ? (
                <a
                  href={(selectedCert.metadata as Record<string, unknown>).downloadUrl as string}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 bg-primary text-on-primary h-12 rounded-2xl flex items-center justify-center gap-2 text-sm font-bold no-underline hover:bg-primary-container active:scale-95 transition-all shadow-level1"
                >
                  <Download size={18} />
                  <Bilingual {...copy.certificates.downloadPdf} />
                </a>
              ) : (
                <button
                  disabled
                  className="flex-1 bg-surface-container-high text-on-surface-variant h-12 rounded-2xl flex items-center justify-center gap-2 text-sm font-bold cursor-not-allowed"
                >
                  <ExternalLink size={18} />
                  <Bilingual {...copy.certificates.unavailable} />
                </button>
              )}
              <button
                onClick={() => setSelectedCert(null)}
                className="px-6 h-12 rounded-2xl border border-on-surface/10 text-on-surface-variant text-sm font-bold hover:bg-surface-container transition-colors"
              >
                <Bilingual {...copy.certificates.close} />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
